import getRetailerCreditSummary from "@faire/web-api/api/retailer/retailerToken/credit-summary/get";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import { IRetailerCreditSummary } from "@faire/web-api/indigofair/data/IRetailerCreditSummary";
import { makeObservable } from "@faire/web/common/makeObservable";
import { moneyOrZero } from "@faire/web/common/money";
import { MoneyMath } from "@faire/web/common/MoneyMath";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { createStoreHook } from "@faire/web/ui/hooks/useStore";
import { action, computed, observable } from "mobx";
import {
  fromPromise,
  FULFILLED,
  IPromiseBasedObservable,
  REJECTED,
} from "mobx-utils";

import {
  FlashSaleOrderSubmitted,
  LimitIncreaseApplicationSubmitted,
  OrderCanceled,
  OrderSubmitted,
  PaidInvoicesSuccess,
} from "@faire/retailer/events";
import { getIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import { fromPromise_resolve } from "@faire/retailer/lib/mobxHelpers";

import { RetailerStore } from "./RetailerStore";

import LimitIncreaseStatus = IRetailerCreditSummary.LimitIncreaseStatus;

/**
 * @deprecated prefer to use `useCreditSummaryStore` or `withStores` instead
 */
export class CreditSummaryStore {
  /**
   * @trackfunction
   */
  static get = singletonGetter(CreditSummaryStore);

  @observable
  private creditSummaryResult?: IPromiseBasedObservable<IRetailerCreditSummary>;

  @computed
  get loaded() {
    return (
      this.creditSummaryResult?.state === FULFILLED ||
      this.creditSummaryResult?.state === REJECTED
    );
  }

  constructor() {
    makeObservable(this);
    this.fetchCreditSummary();

    this.subscribeToAppEvents();
  }

  @computed
  get availableTermsLimit(): IFaireMoney | undefined {
    return this.creditSummary?.available_credit;
  }

  @computed
  get totalTermsLimit(): IFaireMoney {
    return moneyOrZero(this.creditSummary?.total_credit);
  }

  @computed
  get pastDueInvoicesAmount(): number {
    if (this.creditSummary === undefined) {
      return 0;
    }

    return this.creditSummary.total_past_due_amount_cents!;
  }

  @computed
  get limitIncreaseStatus(): keyof typeof IRetailerCreditSummary.LimitIncreaseStatus {
    return (
      this.creditSummary?.limit_increase?.status ||
      IRetailerCreditSummary.LimitIncreaseStatus.NOT_ELIGIBLE
    );
  }

  @computed
  get lastApplicationAt(): number {
    return this.creditSummary?.limit_increase?.last_application_at || 0;
  }

  @computed
  get nextApplicationAllowedAt(): number {
    return this.creditSummary?.limit_increase?.next_application_allowed_at || 0;
  }

  @computed
  get limitIncreaseCents(): number {
    return this.creditSummary?.limit_increase?.on_review_limit_cents || 0;
  }

  @computed
  get potentialLimit(): IFaireMoney | undefined {
    return this.creditSummary?.limit_increase?.next_terms_limit;
  }

  @computed
  get nextAvailableLimits(): IFaireMoney[] | undefined {
    return this.creditSummary?.limit_increase?.next_available_terms_limits;
  }

  @computed
  get previousLimitCents(): IFaireMoney | undefined {
    return this.creditSummary?.previous_terms_limit;
  }

  @computed
  get limitUpdatedAt(): number {
    return this.creditSummary?.limit_updated_at ?? 0;
  }

  @computed
  get availableCreditsIsPositive(): boolean {
    return MoneyMath.greaterThanZero(moneyOrZero(this.availableTermsLimit));
  }

  @action
  fetchCreditSummary = () => {
    const retailerToken = RetailerStore.get().retailerToken;
    if (retailerToken === undefined || getIsBrandPreview()) {
      return;
    }

    this.creditSummaryResult = fromPromise(
      getRetailerCreditSummary(retailerToken)
    );
    return this.creditSummaryResult;
  };

  @action
  setCreditSummary = (creditSummary: IRetailerCreditSummary) => {
    this.creditSummaryResult = fromPromise_resolve(creditSummary);
  };

  get creditSummaryPending() {
    return (
      this.creditSummaryResult === undefined ||
      this.creditSummaryResult.state === "pending"
    );
  }

  private get creditSummary(): IRetailerCreditSummary | undefined {
    return this.creditSummaryResult?.case({ fulfilled: (r) => r });
  }

  get eligibleForLimitIncrease() {
    return this.limitIncreaseStatus !== LimitIncreaseStatus.NOT_ELIGIBLE;
  }

  private subscribeToAppEvents = () => {
    PaidInvoicesSuccess.subscribe(this.fetchCreditSummary);
    OrderCanceled.subscribe(this.fetchCreditSummary);
    OrderSubmitted.subscribe(this.fetchCreditSummary);
    FlashSaleOrderSubmitted.subscribe(this.fetchCreditSummary);
    LimitIncreaseApplicationSubmitted.subscribe(this.fetchCreditSummary);
  };
}

export const useCreditSummaryStore = createStoreHook(CreditSummaryStore);
